// AI assisted development
import { IResponse } from '../../@shared/models/response';

export type ConsolidatedSubsDecision = 'accept' | 'reject';

export interface IItemDecision {
  lineNumber: string;
  approved: boolean;
}

export interface ISubstitutionDecisionGroup {
  originalLineNumber: string;
  substitutedItemDecisions: IItemDecision[];
}

export interface IConsolidatedSubsApprovalRequest {
  substitutionDecisions: ISubstitutionDecisionGroup[];
  addedItemDecisions: IItemDecision[];
}

export interface IGetConsolidatedSubsResponse extends IResponse {
  orderId: number;
  customerName: string;
  customerPhone: string;
  substitutions: IConsolidatedSubsSubstitution[];
  nonOriginalOrderItems: IConsolidatedSubsItem[];
}

export interface IConsolidatedSubsSubstitution {
  originalItem: IConsolidatedSubsItem;
  substitutedItems: IConsolidatedSubsItem[];
}

export interface IConsolidatedSubsItem {
  lineNumber: string;
  orderItemSourceKey: string;
  description: string;
  upc: string;
  quantity: number;
  weight?: number;
}

function pickValue<T>(...values: T[]): T {
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] != null) {
      return values[i];
    }
  }
  return values[values.length - 1];
}

function mapConsolidatedSubsItem(raw: any): IConsolidatedSubsItem {
  if (!raw) {
    return null;
  }

  return {
    lineNumber: pickValue(raw.lineNumber, raw.LineNumber),
    orderItemSourceKey: pickValue(raw.orderItemSourceKey, raw.OrderItemSourceKey),
    description: pickValue(raw.description,raw.Description),
    upc: pickValue(raw.upc, raw.Upc),
    quantity: pickValue(raw.quantity, raw.Quantity),
    weight: pickValue(raw.weight, raw.Weight)
  };
}

function mapConsolidatedSubsSubstitution(raw: any): IConsolidatedSubsSubstitution {
  const substitutedItems = pickValue(raw.substitutedItems, raw.SubstitutedItems, []);

  return {
    originalItem: mapConsolidatedSubsItem(pickValue(raw.originalItem, raw.OriginalItem)),
    substitutedItems: substitutedItems.map(mapConsolidatedSubsItem).filter(Boolean)
  };
}

export function mapGetConsolidatedSubsResponse(raw: any): IGetConsolidatedSubsResponse {
  if (!raw) {
    return null;
  }

  const substitutions = pickValue(raw.substitutions, raw.Substitutions, []);
  const nonOriginalOrderItems = pickValue(raw.nonOriginalOrderItems, raw.NonOriginalOrderItems, []);

  return {
    responseCode: pickValue(raw.responseCode, raw.ResponseCode),
    responseMessage: pickValue(raw.responseMessage, raw.ResponseMessage),
    errors: pickValue(raw.errors, raw.Errors),
    ok: pickValue(raw.ok, raw.Ok),
    orderId: pickValue(raw.orderId, raw.OrderId),
    customerName: pickValue(raw.customerName, raw.CustomerName),
    customerPhone: pickValue(raw.customerPhone, raw.CustomerPhone),
    substitutions: substitutions.map(mapConsolidatedSubsSubstitution),
    nonOriginalOrderItems: nonOriginalOrderItems.map(mapConsolidatedSubsItem).filter(Boolean)
  };
}
